import React, { useMemo, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import {
  AcademicCapIcon,
  ClockIcon,
  CalendarIcon,
  DocumentTextIcon,
  StarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ChartBarIcon,
  BookOpenIcon,
  ArrowLeftIcon,
  PlayIcon,
  LightBulbIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline'
import { StudentPageLayout } from '../../components/student/StudentPageLayout'
import { StudentSection } from '../../components/student/StudentSection'

const ExamDetail: React.FC = () => {
  const { examId } = useParams<{ examId: string }>()
  const navigate = useNavigate()
  
  const [exam] = useState({
    id: examId || '1',
    title: 'JavaScript 基础知识测试',
    subject: 'JavaScript',
    course: {
      title: 'JavaScript 入门课程',
      teacher: '李老师'
    },
    difficulty: 'easy' as const,
    duration: 30,
    totalQuestions: 20,
    type: 'quiz' as const,
    status: 'available' as const,
    dueDate: new Date('2024-02-15'),
    description:
      '这是一个全面的JavaScript基础知识测试，涵盖变量、函数、对象、数组、条件语句与循环等核心概念。',
    instructions: [
      '仔细阅读每个问题，确保理解题意',
      '选择题只能选择一个答案',
      '编程题需要写出完整的代码',
      '考试期间不能使用外部资源',
      '建议合理分配时间，不要在单个问题上花费过多时间'
    ],
    requirements: ['稳定的网络连接', '现代浏览器', '安静的考试环境', '确保设备电量充足'],
    tags: ['基础', '语法', '前端'],
    aiGenerated: true,
    attempts: 0,
    maxAttempts: 3,
    questionTypes: {
      multipleChoice: 12,
      trueFalse: 4,
      shortAnswer: 3,
      essay: 0,
      coding: 1
    },
    topics: ['变量和数据类型', '函数定义和调用', '对象和数组操作', '条件语句和循环', '事件处理基础', 'DOM 操作入门'],
    passingScore: 70
  })

  const [activeTab, setActiveTab] = useState<'overview' | 'preparation'>('overview')

  const layoutMetrics = useMemo(
    () => [
      {
        label: '考试类型',
        value:
          exam.type === 'quiz'
            ? '小测'
            : exam.type === 'midterm'
            ? '期中'
            : exam.type === 'final'
            ? '期末'
            : '练习',
        helper: exam.aiGenerated ? 'AI 组卷' : '人工组卷'
      },
      {
        label: '题目数量',
        value: `${exam.totalQuestions} 题`,
        helper: '覆盖全部章节'
      },
      {
        label: '剩余尝试',
        value: `${Math.max(exam.maxAttempts - exam.attempts, 0)} 次`,
        helper: `共 ${exam.maxAttempts} 次`
      },
      {
        label: '截止时间',
        value: exam.dueDate.toLocaleDateString(),
        helper: exam.dueDate.toLocaleTimeString()
      }
    ],
    [exam]
  )

  const heroExtra = (
    <div className="space-y-3 sm:space-y-4 text-white">
      <p className="text-xs sm:text-sm text-white/80">AI 会捕捉你的备考节奏，并在临界时间自动推送提醒。</p>
      <div className="grid grid-cols-2 gap-2 sm:gap-4">
        <div className="bg-white/10 rounded-xl sm:rounded-2xl border border-white/20 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/70">剩余尝试</p>
          <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1">{Math.max(exam.maxAttempts - exam.attempts, 0)}</p>
          <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1">共 {exam.maxAttempts} 次机会</p>
        </div>
        <div className="bg-white/10 rounded-xl sm:rounded-2xl border border-white/20 p-3 sm:p-4">
          <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] text-white/70">距离截止</p>
          <p className="text-xl sm:text-2xl font-semibold mt-0.5 sm:mt-1">
            {Math.max(Math.ceil((exam.dueDate.getTime() - Date.now()) / (1000 * 60 * 60)), 0)}h
          </p>
          <p className="text-[10px] sm:text-xs text-white/70 mt-0.5 sm:mt-1 truncate">{exam.dueDate.toLocaleString()}</p>
        </div>
      </div>
    </div>
  )

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return 'text-green-600 bg-green-50'
      case 'medium':
        return 'text-yellow-600 bg-yellow-50'
      case 'hard':
        return 'text-red-600 bg-red-50'
      default:
        return 'text-gray-600 bg-gray-50'
    }
  }

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'easy':
        return '简单'
      case 'medium':
        return '中等'
      case 'hard':
        return '困难'
      default:
        return '未知'
    }
  }

  const canTakeExam = exam.status === 'available' && exam.attempts < exam.maxAttempts

  return (
    <StudentPageLayout
      title="考试详情"
      description="查看考试要求、题型分布与考试须知，合理规划学习与开考时间。"
      badge="AI 监考守护"
      metrics={layoutMetrics}
      heroExtra={heroExtra}
      actions={
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
          <button className="px-4 sm:px-5 py-2.5 text-sm font-medium text-white/90 border border-white/40 rounded-xl sm:rounded-2xl hover:bg-white/10 transition flex items-center justify-center">
            <CalendarIcon className="w-4 h-4 mr-2" />
            加入日程
          </button>
          <button className="px-4 sm:px-5 py-2.5 text-sm font-medium text-blue-900 bg-white rounded-xl sm:rounded-2xl hover:bg-blue-50 transition flex items-center justify-center">
            <ClockIcon className="w-4 h-4 mr-2" />
            AI 智能提醒
          </button>
        </div>
      }
    >
      {/* 面包屑导航 - 移动端精简显示 */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 overflow-x-auto pb-1">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 flex-shrink-0">
          <ArrowLeftIcon className="w-4 sm:w-5 h-4 sm:h-5 mr-1" />
          <span className="hidden sm:inline">返回</span>
        </button>
        <span className="text-gray-400 flex-shrink-0">/</span>
        <Link to="/student/exams" className="hover:text-gray-700 flex-shrink-0">
          考试中心
        </Link>
        <span className="text-gray-400 flex-shrink-0">/</span>
        <span className="text-gray-700 truncate max-w-[150px] sm:max-w-none">{exam.title}</span>
      </div>

      <StudentSection title="考试概览" description="考试简介、课程关联与可用操作" icon={AcademicCapIcon}>
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm p-4 sm:p-6">
          {/* 标题和状态 */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4 sm:mb-6">
            <div className="flex-1">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <AcademicCapIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-lg sm:text-2xl font-bold text-gray-900 line-clamp-2">{exam.title}</h1>
                  <p className="text-sm text-gray-600">{exam.subject}</p>
                </div>
              </div>
              {exam.aiGenerated && (
                <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-purple-600 bg-purple-50 rounded-full">
                  <CpuChipIcon className="w-3.5 h-3.5 mr-1" />
                  AI 生成
                </span>
              )}
            </div>

            <div className="flex sm:flex-col gap-2 sm:items-end">
              <span className="px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium text-green-600 bg-green-50">
                {exam.status === 'available' ? '可参加' : '暂不可参加'}
              </span>
              <span className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium ${getDifficultyColor(exam.difficulty)}`}>
                {getDifficultyText(exam.difficulty)}
              </span>
            </div>
          </div>

          {/* 课程信息 */}
          <div className="flex items-center p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl mb-4">
            <BookOpenIcon className="w-5 h-5 sm:w-6 sm:h-6 text-gray-600 mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="font-medium text-gray-900 text-sm sm:text-base truncate">{exam.course.title}</h3>
              <p className="text-xs sm:text-sm text-gray-600">任课老师：{exam.course.teacher}</p>
            </div>
          </div>

          {/* 描述 */}
          <p className="text-sm sm:text-base text-gray-700 mb-4">{exam.description}</p>

          {/* 标签 */}
          <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6">
            {exam.tags.map((tag, index) => (
              <span key={index} className="px-2 py-0.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-full">
                {tag}
              </span>
            ))}
          </div>

          {/* 考试信息统计 - 移动端2x2网格，桌面端4列 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
            <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-xl sm:rounded-2xl">
              <ClockIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-base sm:text-lg font-semibold text-blue-900">{exam.duration}</div>
              <div className="text-xs sm:text-sm text-blue-600">分钟</div>
            </div>
            <div className="text-center p-3 sm:p-4 bg-green-50 rounded-xl sm:rounded-2xl">
              <DocumentTextIcon className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-base sm:text-lg font-semibold text-green-900">{exam.totalQuestions}</div>
              <div className="text-xs sm:text-sm text-green-600">题目</div>
            </div>
            <div className="text-center p-3 sm:p-4 bg-purple-50 rounded-xl sm:rounded-2xl">
              <StarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-base sm:text-lg font-semibold text-purple-900">{exam.passingScore}</div>
              <div className="text-xs sm:text-sm text-purple-600">及格分</div>
            </div>
            <div className="text-center p-3 sm:p-4 bg-yellow-50 rounded-xl sm:rounded-2xl">
              <ChartBarIcon className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 mx-auto mb-1 sm:mb-2" />
              <div className="text-base sm:text-lg font-semibold text-yellow-900">
                {exam.attempts}/{exam.maxAttempts}
              </div>
              <div className="text-xs sm:text-sm text-yellow-600">尝试次数</div>
            </div>
          </div>

          {/* 截止时间提醒 */}
          <div className="flex items-start sm:items-center p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-xl sm:rounded-2xl mb-4 sm:mb-6">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 sm:mr-3 mt-0.5 sm:mt-0 flex-shrink-0" />
            <div className="min-w-0">
              <h4 className="font-medium text-yellow-900 text-sm">截止时间提醒</h4>
              <p className="text-xs sm:text-sm text-yellow-700 truncate">考试截止时间：{exam.dueDate.toLocaleString()}</p>
            </div>
          </div>

          {/* 操作按钮 - 移动端全宽堆叠 */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
            {canTakeExam && (
              <Link
                to={`/student/exams/${exam.id}/take`}
                className="flex items-center justify-center px-5 py-3 sm:py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                <PlayIcon className="w-5 h-5 mr-2" />
                开始考试
              </Link>
            )}
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center px-5 py-3 sm:py-2.5 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              返回列表
            </button>
          </div>
        </div>
      </StudentSection>

      <StudentSection title="题型与准备" description="熟悉题型分布、知识点与技术要求" icon={InformationCircleIcon}>
        <div className="bg-white rounded-2xl sm:rounded-3xl border border-gray-100 shadow-sm">
          {/* 选项卡导航 - 移动端可滚动 */}
          <div className="border-b overflow-x-auto scrollbar-hide">
            <nav className="flex min-w-max px-4 sm:px-6">
              {[
                { key: 'overview', label: '考试概览', icon: InformationCircleIcon },
                { key: 'preparation', label: '考试准备', icon: LightBulbIcon }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as 'overview' | 'preparation')}
                  className={`py-3 sm:py-4 px-4 sm:px-6 border-b-2 font-medium text-sm flex items-center whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-1.5 sm:mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-4 sm:p-6">
            {activeTab === 'overview' && (
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">题型分布</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-4">
                    {Object.entries(exam.questionTypes).map(([type, count]) => {
                      if (count === 0) return null
                      const typeNames: Record<string, string> = {
                        multipleChoice: '选择题',
                        trueFalse: '判断题',
                        shortAnswer: '简答题',
                        essay: '论述题',
                        coding: '编程题'
                      }
                      return (
                        <div key={type} className="p-3 sm:p-4 bg-gray-50 rounded-xl sm:rounded-2xl">
                          <h4 className="font-medium text-gray-900 text-sm">{typeNames[type] || type}</h4>
                          <p className="text-xl sm:text-2xl font-bold text-blue-600">{count}</p>
                          <p className="text-xs sm:text-sm text-gray-500">题</p>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">考试知识点</h3>
                  <div className="space-y-2">
                    {exam.topics.map((topic, index) => (
                      <div key={index} className="flex items-center p-2.5 sm:p-3 bg-blue-50 rounded-xl sm:rounded-2xl">
                        <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-2 sm:mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-900">{topic}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preparation' && (
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">考试须知</h3>
                  <div className="space-y-2">
                    {exam.instructions.map((instruction, index) => (
                      <div key={index} className="flex items-start p-2.5 sm:p-3 bg-yellow-50 rounded-xl sm:rounded-2xl">
                        <InformationCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-900">{instruction}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">技术要求</h3>
                  <div className="space-y-2">
                    {exam.requirements.map((requirement, index) => (
                      <div key={index} className="flex items-start p-2.5 sm:p-3 bg-green-50 rounded-xl sm:rounded-2xl">
                        <CheckCircleIcon className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-2 sm:mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-900">{requirement}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-xl sm:rounded-2xl">
                  <h4 className="font-medium text-blue-900 mb-2 text-sm sm:text-base">💡 考试建议</h4>
                  <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
                    <li>• 开考前 15 分钟检查设备与网络</li>
                    <li>• 仔细阅读题目，避免理解偏差</li>
                    <li>• 每题平均用时 {Math.round(exam.duration / exam.totalQuestions)} 分钟</li>
                    <li>• 难题先标记，最后集中处理</li>
                    <li>• 提交前务必复查所有答案</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      </StudentSection>
    </StudentPageLayout>
  )
}

export default ExamDetail

